/**
 * Boveda de fotos DENTRO de la app (Directorio Data de Android).
 * Solo en Capacitor nativo (APK): descarga el catalogo a disco.
 * En web (navegador): no hace fetch->blob->disco; el <img src> basta
 * (evita cientos de errores CORS vendedor.* → api host /imagenes/).
 */
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';

const FOLDER = 'oh-photo-vault-v3';
const DIR = Directory.Data;
const INDEX_KEY = 'oh_photo_vault_index_v3';
const SYNC_META_KEY = 'oh_photo_vault_sync_v3';
const memory = new Map();
const inflight = new Map();
/** URLs que fallaron (CORS/HTTP): no reintentar en bucle ni spamear consola. */
const failedRemotes = new Map(); // key -> failedAt
const MAX_PARALLEL = 4;
const FAIL_TTL_MS = 60 * 1000;
let activeFetches = 0;
const waitQueue = [];

function acquireSlot() {
  if (activeFetches < MAX_PARALLEL) {
    activeFetches += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => waitQueue.push(resolve)).then(() => {
    activeFetches += 1;
  });
}

function releaseSlot() {
  activeFetches = Math.max(0, activeFetches - 1);
  const next = waitQueue.shift();
  if (next) next();
}

/** True solo en APK Capacitor. En web del navegador es false. */
export function isNativePhotoVault() {
  try {
    return typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function isOnline() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

export function remoteImageUrl(url) {
  return url ? String(url).trim() : '';
}

/** Path + ?v= : si solo cambia el query, hay que volver a bajar (910.png era pomos). */
export function normalizeImageKey(url) {
  const remote = remoteImageUrl(url);
  if (!remote) return '';
  try {
    const u = new URL(remote, 'https://api.ohbellezaoficial.com');
    const v = u.searchParams.get('v') || '';
    return `${u.pathname.replace(/\/+/g, '/').toLowerCase()}|v=${v}`;
  } catch {
    return remote.toLowerCase();
  }
}

function failKey(url) {
  return normalizeImageKey(url) || remoteImageUrl(url);
}

function markFailed(url) {
  const key = failKey(url);
  if (key) failedRemotes.set(key, Date.now());
}

function hasFailed(url) {
  const key = failKey(url);
  if (!key) return false;
  const at = failedRemotes.get(key);
  if (!at) return false;
  if (Date.now() - at > FAIL_TTL_MS) {
    failedRemotes.delete(key);
    return false;
  }
  return true;
}

export function peekCachedImage(url) {
  const remote = remoteImageUrl(url);
  if (!remote) return null;
  return memory.get(remote) || memory.get(normalizeImageKey(remote)) || null;
}

function filePathFor(url) {
  const key = normalizeImageKey(url) || remoteImageUrl(url);
  let h = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const ext = /\.png(\?|$)/i.test(url) ? 'png' : /\.webp(\?|$)/i.test(url) ? 'webp' : 'jpg';
  return `${FOLDER}/${(h >>> 0).toString(16)}.${ext}`;
}

function mimeFor(path) {
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.webp')) return 'image/webp';
  return 'image/jpeg';
}

function readIndex() {
  try {
    const raw = JSON.parse(localStorage.getItem(INDEX_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

function writeIndex(map) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(map));
  } catch {
    /* quota */
  }
}

function rememberUrl(remote) {
  const map = readIndex();
  map[normalizeImageKey(remote) || remote] = remote;
  writeIndex(map);
}

export function listKnownImageUrls() {
  if (!isNativePhotoVault()) return [];
  return Object.values(readIndex()).filter(Boolean);
}

export function getPhotoVaultStats() {
  if (!isNativePhotoVault()) return { saved: 0 };
  return { saved: Object.keys(readIndex()).length };
}

export function getLastSyncMeta() {
  if (!isNativePhotoVault()) return null;
  try {
    return JSON.parse(localStorage.getItem(SYNC_META_KEY) || 'null');
  } catch {
    return null;
  }
}

function setLastSyncMeta(meta) {
  try {
    localStorage.setItem(SYNC_META_KEY, JSON.stringify(meta));
  } catch {
    /* ignore */
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || '');
      const b64 = result.includes(',') ? result.split(',')[1] : result;
      if (!b64) reject(new Error('empty base64'));
      else resolve(b64);
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

function base64ToObjectUrl(base64, mime) {
  const clean = String(base64 || '').replace(/^data:[^;]+;base64,/, '');
  const bin = atob(clean);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([arr], { type: mime || 'image/jpeg' }));
}

function rememberInMemory(remote, objectUrl) {
  memory.set(remote, objectUrl);
  const key = normalizeImageKey(remote);
  if (key) memory.set(key, objectUrl);
}

async function readFromDisk(remote) {
  if (!isNativePhotoVault()) return null;
  const path = filePathFor(remote);
  try {
    const { data } = await Filesystem.readFile({ path, directory: DIR });
    if (!data) return null;
    const objectUrl = base64ToObjectUrl(String(data), mimeFor(path));
    rememberInMemory(remote, objectUrl);
    rememberUrl(remote);
    return objectUrl;
  } catch {
    return null;
  }
}

async function writeToDisk(remote, blob) {
  if (!isNativePhotoVault() || !blob?.size) return false;
  const path = filePathFor(remote);
  try {
    await Filesystem.mkdir({ path: FOLDER, directory: DIR, recursive: true }).catch(() => {});
    const data = await blobToBase64(blob);
    await Filesystem.writeFile({ path, data, directory: DIR, recursive: true });
    rememberUrl(remote);
    return true;
  } catch {
    return false;
  }
}

function base64ResponseToBlob(data, contentType) {
  const clean = String(data || '').replace(/^data:[^;]+;base64,/, '');
  const bin = atob(clean);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  const mime = (contentType || 'image/jpeg').split(';')[0].trim() || 'image/jpeg';
  return new Blob([arr], { type: mime });
}

async function fetchBlob(remote) {
  if (hasFailed(remote)) {
    const err = new Error('skip-failed');
    err.quiet = true;
    throw err;
  }

  if (Capacitor.isNativePlatform()) {
    try {
      const res = await CapacitorHttp.get({
        url: remote,
        responseType: 'arraybuffer',
        connectTimeout: 25000,
        readTimeout: 35000,
      });
      if (res.status < 200 || res.status >= 300) throw new Error(`HTTP ${res.status}`);
      const ct = res.headers?.['Content-Type'] || res.headers?.['content-type'] || 'image/jpeg';
      const blob = base64ResponseToBlob(res.data, ct);
      if (!blob?.size) throw new Error('empty');
      return blob;
    } catch {
      /* fallback a fetch (puede fallar por CORS en algunos WebViews) */
    }
  }

  try {
    const res = await fetch(remote, { mode: 'cors', credentials: 'omit', cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    if (!blob?.size) throw new Error('empty');
    return blob;
  } catch (err) {
    markFailed(remote);
    const quiet = new Error(err?.message || 'fetch-failed');
    quiet.quiet = true;
    throw quiet;
  }
}

export function warmProductImage(url) {
  if (!isNativePhotoVault()) return;
  const remote = remoteImageUrl(url);
  if (!remote || peekCachedImage(remote) || hasFailed(remote)) return;
  resolveCachedImage(remote).catch(() => {});
}

export function warmProductImages(urls = [], { limit = 80 } = {}) {
  if (!isNativePhotoVault()) return;
  const list = [...new Set((urls || []).map(remoteImageUrl).filter(Boolean))].slice(0, limit);
  for (const u of list) {
    if (!peekCachedImage(u) && !hasFailed(u)) warmProductImage(u);
  }
}

export async function persistProductImages(urls = [], { limit = 5000, onProgress } = {}) {
  if (!isNativePhotoVault()) {
    return { saved: 0, failed: 0, total: 0 };
  }
  const list = [...new Set((urls || []).map(remoteImageUrl).filter(Boolean))].slice(0, limit);
  let saved = 0;
  let failed = 0;
  let i = 0;
  let active = 0;

  await new Promise((done) => {
    function pump() {
      while (active < MAX_PARALLEL && i < list.length) {
        const u = list[i++];
        active += 1;
        (async () => {
          try {
            if (peekCachedImage(u) || (await readFromDisk(u))) {
              saved += 1;
            } else if (hasFailed(u)) {
              failed += 1;
            } else if (isOnline()) {
              const blob = await fetchBlob(u);
              await writeToDisk(u, blob);
              rememberInMemory(u, URL.createObjectURL(blob));
              saved += 1;
            } else {
              failed += 1;
            }
          } catch {
            failed += 1;
          } finally {
            active -= 1;
            onProgress?.({ done: saved + failed, total: list.length, saved, failed });
            if (i >= list.length && active === 0) done();
            else pump();
          }
        })();
      }
      if (i >= list.length && active === 0) done();
    }
    if (!list.length) done();
    else pump();
  });

  setLastSyncMeta({ at: Date.now(), saved, failed, total: list.length });
  return { saved, failed, total: list.length };
}

export async function hydrateOfflineImages(urls = [], { limit = 5000 } = {}) {
  if (!isNativePhotoVault()) return;
  const list = [...new Set([...(urls || []), ...listKnownImageUrls()].map(remoteImageUrl).filter(Boolean))].slice(
    0,
    limit
  );
  let i = 0;
  let active = 0;
  await new Promise((done) => {
    function pump() {
      while (active < MAX_PARALLEL && i < list.length) {
        const u = list[i++];
        if (peekCachedImage(u)) continue;
        active += 1;
        readFromDisk(u)
          .catch(() => null)
          .finally(() => {
            active -= 1;
            if (i >= list.length && active === 0) done();
            else pump();
          });
      }
      if (i >= list.length && active === 0) done();
    }
    if (!list.length) done();
    else pump();
  });
}

export async function resolveCachedImage(url) {
  const remote = remoteImageUrl(url);
  if (!remote) return null;
  // Web: no bóveda offline — el caller usa <img src={remote}>.
  if (!isNativePhotoVault()) return remote;

  const peeked = peekCachedImage(remote);
  if (peeked) return peeked;
  if (hasFailed(remote)) return remote;
  if (inflight.has(remote)) return inflight.get(remote);

  const job = (async () => {
    await acquireSlot();
    try {
      const disk = await readFromDisk(remote);
      if (disk) return disk;
      if (!isOnline()) return null;
      try {
        const blob = await fetchBlob(remote);
        writeToDisk(remote, blob).catch(() => {});
        const objectUrl = URL.createObjectURL(blob);
        rememberInMemory(remote, objectUrl);
        return objectUrl;
      } catch {
        markFailed(remote);
        return remote;
      }
    } finally {
      releaseSlot();
      inflight.delete(remote);
    }
  })();

  inflight.set(remote, job);
  return job;
}
